import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faMapMarkerAlt, faSyncAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const SupervisorMap = () => {
    const [supervisors, setSupervisors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [mapMarkers, setMapMarkers] = useState([]);

    useEffect(() => {
        fetchSupervisors();
        
        // Refresh data when page becomes visible (user navigates back from create supervisor)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchSupervisors();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const fetchSupervisors = async () => {
        try {
            setLoading(true);
            const response = await api.get('/supervisors');
            const supervisorsData = response.data.data || response.data; // Handle both paginated and non-paginated responses
            
            // Filter supervisors that have map coordinates
            const supervisorsWithCoords = supervisorsData.filter(supervisor => 
                supervisor.map_lat && supervisor.map_lng
            );
            
            setSupervisors(supervisorsWithCoords);
        } catch (error) {
            console.error('Error fetching supervisors:', error);
            setError('Erreur lors du chargement des prestataires');
        } finally {
            setLoading(false);
        }
    };

    const addMarkersToMap = (map, supervisorsData) => {
        console.log('Adding markers to map:', supervisorsData.length);
        console.log('Supervisors data:', supervisorsData);
        
        // Group supervisors by location (lat, lng)
        const supervisorsByLocation = {};
        supervisorsData.forEach((supervisor) => {
            console.log('Processing supervisor:', supervisor.name, 'coords:', supervisor.map_lat, supervisor.map_lng);
            if (supervisor.map_lat && supervisor.map_lng) {
                const key = `${supervisor.map_lat},${supervisor.map_lng}`;
                if (!supervisorsByLocation[key]) {
                    supervisorsByLocation[key] = [];
                }
                supervisorsByLocation[key].push(supervisor);
                console.log('Added to location:', key);
            } else {
                console.log('Skipping supervisor (no coordinates):', supervisor.name);
            }
        });
        
        console.log('Grouped supervisors:', supervisorsByLocation);
        
        // Create markers for each location
        Object.entries(supervisorsByLocation).forEach(([locationKey, supervisorsAtLocation]) => {
            console.log('Creating marker for location:', locationKey, 'with supervisors:', supervisorsAtLocation.length);
            const [lat, lng] = locationKey.split(',').map(Number);
            
            // Create custom marker icon for all supervisors
            let markerIcon;
            if (supervisorsAtLocation.length > 1) {
                console.log('Creating count badge for multiple supervisors');
                // Create a custom icon with count badge for multiple supervisors
                const iconHtml = `
                    <div style="
                        background: #dc2626;
                        width: 30px;
                        height: 30px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-weight: bold;
                        font-size: 12px;
                        border: 2px solid white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                    ">
                        ${supervisorsAtLocation.length}
                    </div>
                `;
                
                markerIcon = window.L.divIcon({
                    html: iconHtml,
                    className: 'custom-marker',
                    iconSize: [30, 30],
                    iconAnchor: [15, 15]
                });
            } else {
                console.log('Creating standard marker for single supervisor');
                // Create a standard red marker for single supervisors
                const iconHtml = `
                    <div style="
                        background: #dc2626;
                        width: 25px;
                        height: 25px;
                        border-radius: 50% 50% 50% 0;
                        transform: rotate(-45deg);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 2px solid white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                    ">
                        <div style="
                            width: 8px;
                            height: 8px;
                            background: white;
                            border-radius: 50%;
                            transform: rotate(45deg);
                        "></div>
                    </div>
                `;
                
                markerIcon = window.L.divIcon({
                    html: iconHtml,
                    className: 'single-marker',
                    iconSize: [25, 25],
                    iconAnchor: [12, 25]
                });
            }
            
            const marker = window.L.marker([lat, lng], { icon: markerIcon }).addTo(map);
            console.log('Marker added to map at:', lat, lng, 'for', supervisorsAtLocation.length, 'supervisor(s)');
            console.log('Supervisors at this location:', supervisorsAtLocation.map(s => s.name));
            
            // Create popup content based on number of supervisors
            let popupContent;
            
            if (supervisorsAtLocation.length === 1) {
                // Single supervisor - show full details
                const supervisor = supervisorsAtLocation[0];
                popupContent = `
                    <div class="p-2">
                        <h3 class="font-bold text-gray-900 mb-2">${supervisor.name}</h3>
                        <p class="text-sm text-gray-600 mb-1"><strong>Email:</strong> ${supervisor.email}</p>
                        <p class="text-sm text-gray-600 mb-1"><strong>Ville:</strong> ${supervisor.city_name || supervisor.location_name || 'Non spécifiée'}</p>
                        <p class="text-sm text-gray-600 mb-1"><strong>Région:</strong> ${supervisor.city_region || supervisor.region || 'Non spécifiée'}</p>
                        <p class="text-sm text-gray-600 mb-2"><strong>Dernière connexion:</strong> ${supervisor.last_login ? new Date(supervisor.last_login).toLocaleDateString('fr-FR') : 'Jamais'}</p>
                        <button onclick="navigateToSupervisor(${supervisor.id})" 
                                class="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors">
                            Voir les détails
                        </button>
                    </div>
                `;
            } else {
                // Multiple supervisors - show summary with buttons only
                const firstSupervisor = supervisorsAtLocation[0];
                const cityName = firstSupervisor.city_name || firstSupervisor.location_name || 'Non spécifiée';
                const regionName = firstSupervisor.city_region || firstSupervisor.region || 'Non spécifiée';
                
                popupContent = `
                    <div class="p-2">
                        <h3 class="font-bold text-gray-900 mb-2">${cityName}</h3>
                        <p class="text-sm text-gray-600 mb-1"><strong>Région:</strong> ${regionName}</p>
                        <p class="text-sm text-gray-600 mb-3"><strong>Prestataires:</strong> ${supervisorsAtLocation.length}</p>
                        <div class="space-y-1">
                            ${supervisorsAtLocation.map(supervisor => `
                                <button onclick="navigateToSupervisor(${supervisor.id})" 
                                        class="block w-full bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 transition-colors">
                                    ${supervisor.name}
                                </button>
                            `).join('')}
                        </div>
                    </div>
                `;
            }
            
            marker.bindPopup(popupContent);
        });
        
        console.log(`Markers added successfully: ${Object.keys(supervisorsByLocation).length} locations, ${supervisorsData.length} supervisors`);
    };

    const initializeMap = () => {
        // Wait for DOM to be ready and check if container exists
        const container = document.getElementById('supervisor-map');
        if (!container) {
            console.log('Map container not found, retrying...');
            setTimeout(initializeMap, 100);
            return;
        }

        // Check if map is already initialized
        if (window.supervisorMap) {
            console.log('Map already initialized, skipping re-initialization');
            return;
        }

        // Check if Leaflet is available
        if (typeof window !== 'undefined' && window.L) {
            try {
                // Initialize the map centered on Morocco
                const map = window.L.map('supervisor-map').setView([31.7917, -7.0926], 6);

                // Add OpenStreetMap tiles
                window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                }).addTo(map);

                // Add markers for supervisors
                addMarkersToMap(map, supervisors);

                // Store map reference for potential future use
                window.supervisorMap = map;
                console.log('Map initialized successfully');
            } catch (error) {
                console.error('Error initializing map:', error);
            }
        } else {
            console.log('Leaflet not available yet, retrying...');
            setTimeout(initializeMap, 100);
        }
    };

    // Initialize map when component is mounted
    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => {
                initializeMap();
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [loading]); // Only depend on loading, not supervisors

    // Update markers when supervisors data changes
    useEffect(() => {
        if (window.supervisorMap && !loading && supervisors.length >= 0) {
            console.log('Updating markers for', supervisors.length, 'supervisors');
            // Clear existing markers
            let markersRemoved = 0;
            window.supervisorMap.eachLayer((layer) => {
                if (layer instanceof window.L.Marker) {
                    window.supervisorMap.removeLayer(layer);
                    markersRemoved++;
                }
            });
            console.log('Removed', markersRemoved, 'existing markers');
            // Add new markers
            addMarkersToMap(window.supervisorMap, supervisors);
        }
    }, [supervisors]); // Only depend on supervisors data

    // Global function for navigation (accessible from popup)
    useEffect(() => {
        window.navigateToSupervisor = (supervisorId) => {
            navigate(`/supervisors/${supervisorId}`);
        };

        return () => {
            delete window.navigateToSupervisor;
            // Clean up map instance
            if (window.supervisorMap) {
                window.supervisorMap.remove();
                delete window.supervisorMap;
            }
        };
    }, [navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center">
                {/* Dotted Background */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement de la carte des prestataires...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white shadow-xl rounded-lg">
                    {/* Header */}
                    <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Carte des Prestataires</h1>
                                <p className="mt-1 text-sm text-gray-600">
                                    Visualisation géographique des prestataires au Maroc
                                </p>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <button
                                    onClick={() => navigate('/supervisors')}
                                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center sm:justify-start"
                                >
                                    <span className="mr-2">←</span>
                                    Liste des prestataires
                                </button>
                                
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Map Container */}
                    <div className="p-6">
                        <div className="mb-4">
                            <p className="text-sm text-gray-600">
                                <strong>{supervisors.length}</strong> prestataire(s) dans <strong>{new Set(supervisors.filter(s => s.map_lat && s.map_lng).map(s => `${s.map_lat},${s.map_lng}`)).size}</strong> emplacement(s) affiché(s) sur la carte
                            </p>
                        </div>
                        
                        {/* Map */}
                        <div 
                            id="supervisor-map" 
                            className="w-full h-96 sm:h-[500px] lg:h-[600px] rounded-lg border border-gray-300 shadow-sm"
                        ></div>
                        
                        {/* Instructions */}
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                            <h3 className="text-sm font-medium text-blue-900 mb-2">Instructions d'utilisation :</h3>
                            <ul className="text-sm text-blue-700 space-y-1">
                                <li>• Cliquez sur les marqueurs pour voir les informations du prestataire</li>
                                <li>• Utilisez les contrôles de zoom pour naviguer sur la carte</li>
                                <li>• Cliquez sur "Voir les détails" pour accéder à la page complète du prestataire</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupervisorMap;
